import ThemeToggle from "../components/ThemeToggle";

interface LegalPageProps {
  page: "privacy" | "terms";
  theme: { theme: "light" | "dark"; toggle: () => void };
}

const UPDATED = "May 31, 2026";

export default function LegalPage({ page, theme }: LegalPageProps) {
  const isPrivacy = page === "privacy";

  return (
    <div className="min-h-dvh">
      <header className="border-b border-zinc-200 bg-zinc-50/85 backdrop-blur dark:border-zinc-800 dark:bg-zinc-950/80">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
          <a href="/" className="flex items-center gap-2">
            <img
              src="/icons/strava-app-icon-124.png"
              alt=""
              className="h-7 w-7"
              aria-hidden="true"
            />
            <span className="text-sm font-semibold tracking-tight">
              Fitness RPG
            </span>
          </a>
          <ThemeToggle theme={theme} />
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-10">
        <p className="text-sm font-medium text-accent">
          Last updated {UPDATED}
        </p>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight">
          {isPrivacy ? "Privacy Policy" : "Terms of Use"}
        </h1>
        {isPrivacy ? <PrivacyPolicy /> : <TermsOfUse />}
      </main>
    </div>
  );
}

function PrivacyPolicy() {
  return (
    <div className="mt-8 space-y-7 text-sm leading-7 text-zinc-700 dark:text-zinc-300">
      <p>
        Fitness RPG is a private fitness quest dashboard. It turns activity
        records into routes, badges, items, and progress summaries.
      </p>

      <h2 className="text-lg font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">Data We Collect</h2>
      <p>
        Depending on what you connect or enter, Fitness RPG may process your
        Google profile, Strava profile, OAuth identifiers, email address,
        activity distance, steps, Active Zone Minutes, heart-rate data, sleep
        data, calorie and distance summaries, manual walk entries, weight
        records, quest progress, badges, and inventory items.
      </p>

      <h2 className="text-lg font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">How We Use Data</h2>
      <p>
        We use this data only to sign you in, show your private dashboard,
        calculate quest progress, store your preferences, and sync your quest
        state. We do not sell personal data, run ads, or use fitness data for
        marketing profiles.
      </p>

      <h2 className="text-lg font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">Google Health And Strava</h2>
      <p>
        When you connect Google Health or Strava, you authorize those providers
        to send Fitness RPG the data needed for the app features you use.
        Google Health powers Fitbit-style health metrics when available. Strava
        supplies activity records such as distance, moving time, and activity
        type. You can revoke provider access from your Google or Strava account
        settings.
      </p>

      <h2 className="text-lg font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">Storage And Service Providers</h2>
      <p>
        Fitness RPG runs on Cloudflare Pages. Session data, OAuth tokens, manual
        entries, and quest state are stored server-side in Cloudflare KV. Tokens
        are used only to refresh provider access for your account.
      </p>

      <h2 className="text-lg font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">Cookies And Local Storage</h2>
      <p>
        Fitness RPG uses necessary session cookies and browser storage for
        sign-in, theme, preferences, and app state. Cloudflare may also use
        strictly necessary security or delivery cookies. We do not currently use
        analytics cookies, advertising cookies, or tracking pixels.
      </p>

      <h2 className="text-lg font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">Sharing</h2>
      <p>
        Your dashboard is private by default. If you use a share feature, the
        app may create a shareable card or export from the progress, item, or
        badge data you choose to share. We do not send your private health data
        to other people unless you choose to share it.
      </p>

      <h2 className="text-lg font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">Deletion And Export</h2>
      <p>
        You can disconnect Google or Strava from the provider account settings.
        You can export quest progress from the app where export controls are
        available. To request deletion of server-side Fitness RPG records,
        contact the app owner.
      </p>

      <h2 className="text-lg font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">Children</h2>
      <p>
        Fitness RPG is not intended for children under 13 and should not be used
        with a child&apos;s Google, Strava, or health data account.
      </p>

      <h2 className="text-lg font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">Changes</h2>
      <p>
        We may update this policy as the app changes. Material changes should
        be reflected here before new categories of sensitive data are collected
        or used in new ways.
      </p>
    </div>
  );
}

function TermsOfUse() {
  return (
    <div className="mt-8 space-y-7 text-sm leading-7 text-zinc-700 dark:text-zinc-300">
      <p>
        These terms describe the intended use of Fitness RPG. By using the app,
        you agree to use it responsibly and understand its limits.
      </p>

      <h2 className="text-lg font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">Private Fitness Entertainment</h2>
      <p>
        Fitness RPG is a personal fitness and entertainment dashboard. Quest
        rewards, XP, badges, chests, items, and route progress are game
        mechanics. They do not have real-world value.
      </p>

      <h2 className="text-lg font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">Not Medical Advice</h2>
      <p>
        Fitness RPG is not a medical device and does not provide medical,
        nutrition, diagnosis, treatment, or training advice. Talk to a qualified
        professional before changing exercise, sleep, weight, or health habits.
      </p>

      <h2 className="text-lg font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">Data Accuracy</h2>
      <p>
        Fitness and health data may be delayed, missing, duplicated, estimated,
        or incorrect. Google, Fitbit, Strava, manual entries, and device syncs
        can all change historical values. Quest progress should be treated as a
        fun reflection of activity, not an authoritative health record.
      </p>

      <h2 className="text-lg font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">Manual Entries</h2>
      <p>
        Manual walk and weight entries are user-controlled. The app is not
        designed to prevent cheating or to verify that manually entered data is
        accurate.
      </p>

      <h2 className="text-lg font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">Provider Terms</h2>
      <p>
        If you connect Google Health or Strava, your use of those services
        remains subject to their own terms, privacy policies, and account
        settings. Fitness RPG is not sponsored or endorsed by Google, Fitbit,
        Strava, NASA, the National Park Service, or trail organizations.
      </p>

      <h2 className="text-lg font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">Availability</h2>
      <p>
        The app may change, break, lose access to provider APIs, or be
        discontinued. We may update features, routes, assets, requirements, and
        storage behavior as the project evolves.
      </p>

      <h2 className="text-lg font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">Safe Use</h2>
      <p>
        Use common sense while exercising. Do not use quests, streaks, chests,
        or badges as a reason to ignore pain, unsafe conditions, medical advice,
        or local rules.
      </p>
    </div>
  );
}
